import * as React from 'react';
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	Link,
	Container,
	Alert,
	IconButton,
	InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AppDispatch } from "../../redux/store";
import { login } from "../../redux/actions/authActions";
import { useNavigate, useSearchParams } from "react-router-dom";
import ApiBase from "../../api/apiBase";
import { jwtDecode } from "jwt-decode";
import CacheService from '../../services/CacheService';

// CustomJwtPayload interface'ini ekleyelim
interface CustomJwtPayload {
	exp: number;
	iss: string;
	nbf: number;
	'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
	'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
	'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
	permissions: string[];
}

export default function Login() {
	const dispatch = AppDispatch();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [showPassword, setShowPassword] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [success, setSuccess] = React.useState<string | null>(null);
	const [loading, setLoading] = React.useState(false);

	// Email doğrulama başarısını kontrol et
	React.useEffect(() => {
		if (searchParams.get('verified') === 'true') {
			setSuccess('✅ E-posta doğrulama başarılı! Artık hesabınızla giriş yapabilirsiniz.');
		}
	}, [searchParams]);

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setLoading(true);
		
		const data = new FormData(event.currentTarget);
		const credentials = {
			email: data.get('email'),
			password: data.get('password'),
		};

		ApiBase.login(credentials)
			.then((resp: any) => {
				// Response'u logla
				console.log('Full Login Response:', resp);
				
				// Farklı response formatlarını kontrol et
				const token = resp.accessToken?.token || resp.token;
				const permissions = resp.permissions || [];
				const emailVerified = resp.emailVerified || resp.data?.emailVerified;

				// Debug log
				console.log('Login Response:', { 
					emailVerified, 
					email: credentials.email,
					token: token ? 'exists' : 'missing',
					fullResponse: resp
				});

				// Email verified kontrolü - false, null, undefined veya falsy değerleri kontrol et
				if (emailVerified === false || emailVerified === null || emailVerified === undefined) {
					// Email doğrulanmamış - verification sayfasına yönlendir
					console.log('Email not verified, redirecting to verification page...');
					setLoading(false);
					
					// Token'ı kaydetme - email doğrulanmamış kullanıcılar için token kaydedilmemeli
					
					// Yönlendirmeyi yap
					setTimeout(() => {
						navigate('/verification', { 
							replace: true,
							state: { 
								email: credentials.email as string,
								password: credentials.password as string 
							} 
						});
					}, 100);
					return;
				}

				if (!token || typeof token !== "string") {
					throw new Error("Invalid token: Token is missing or not a string");
				}

				// Decode token to get userId
				const decodedToken = jwtDecode<CustomJwtPayload>(token);
				const userId = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
				
				// Önce tüm önbelleği temizle (önceki session'dan kalan verileri temizle)
				CacheService.clearAll();
				
				// Save token and userId to localStorage and CacheService
				localStorage.setItem("authToken", token);
				localStorage.setItem("userId", userId);
				CacheService.setAuthToken(token);
				
				// CRITICAL: Set authorization header BEFORE dispatching and navigating
				ApiBase.putAccessTokenInfo(token);

				dispatch(
					login({
						accessToken: token,
						permissions,
					})
				);

				console.log('✅ Login: Auth setup completed, navigating to home...');
				
				// Small delay to ensure state is updated before navigation
				setTimeout(() => {
					navigate("/");
				}, 50);
			})
			.catch((error: any) => {
				console.error("Login Error:", error);
				setError("Giriş başarısız. Lütfen bilgilerinizi kontrol edin.");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				backgroundColor: '#ffffff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 2,
			}}
		>
			<Container maxWidth="xs">
				<Card
					elevation={3}
					sx={{
						borderRadius: 3,
						backgroundColor: '#ffffff',
						border: '1px solid #e0e0e0',
					}}
				>
					<CardContent sx={{ p: 4 }}>
						{/* Login Form */}
						<Box component="form" onSubmit={handleSubmit} noValidate>
							<Typography
								variant="h5"
								component="h2"
								gutterBottom
								sx={{
									fontWeight: 600,
									color: '#094151',
									mb: 3,
								}}
							>
								Giriş Yap
							</Typography>

							{error && (
								<Alert severity="error" sx={{ mb: 3 }}>
									{error}
								</Alert>
							)}

							{success && (
								<Alert severity="success" sx={{ mb: 3 }}>
									{success}
								</Alert>
							)}

							<Box mb={3}>
								<Typography
									variant="subtitle2"
									sx={{ mb: 1, fontWeight: 600, color: '#333' }}
								>
									E-posta
								</Typography>
								<TextField
									required
									fullWidth
									id="email"
									name="email"
									type="email"
									placeholder="Email adresinizi giriniz"
									variant="outlined"
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 2,
											backgroundColor: '#f8f9ff',
											'&:hover fieldset': {
												borderColor: '#094151',
											},
											'&.Mui-focused fieldset': {
												borderColor: '#094151',
											},
										},
									}}
								/>
							</Box>

							<Box mb={4}>
								<Typography
									variant="subtitle2"
									sx={{ mb: 1, fontWeight: 600, color: '#333' }}
								>
									Şifre
								</Typography>
								<TextField
									required
									fullWidth
									name="password"
									type={showPassword ? 'text' : 'password'}
									id="password"
									placeholder="••••••••••••"
									variant="outlined"
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="toggle password visibility"
													onClick={handleClickShowPassword}
													edge="end"
												>
													{showPassword ? <VisibilityOff /> : <Visibility />}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										'& .MuiOutlinedInput-root': {
											borderRadius: 2,
											backgroundColor: '#f8f9ff',
											'&:hover fieldset': {
												borderColor: '#094151',
											},
											'&.Mui-focused fieldset': {
												borderColor: '#094151',
											},
										},
									}}
								/>
							</Box>

							<Button
								type="submit"
								fullWidth
								variant="contained"
								disabled={loading}
								sx={{
									mt: 2,
									mb: 4,
									py: 1.5,
									backgroundColor: '#094151',
									color: '#fff',
									fontWeight: 600,
									borderRadius: '8px',
									boxShadow: 'none',
									'&:hover': {
										backgroundColor: '#022631',
										color: '#fff',
									},
									'&:disabled': {
										backgroundColor: '#ccc',
									},
								}}
							>
								{loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
							</Button>

							{/* Footer Links */}
							<Box
								display="flex"
								justifyContent="space-between"
								alignItems="center"
								flexWrap="wrap"
								gap={2}
							>
								<Link
									href="#"
									variant="body2"
									sx={{
										color: '#094151',
										textDecoration: 'none',
										fontWeight: 500,
										'&:hover': {
											textDecoration: 'underline',
										},
									}}
									onClick={(e) => {
										e.preventDefault();
										navigate('/forgot-password');
									}}
								>
									Şifremi unuttum?
								</Link>

							</Box>
						</Box>
					</CardContent>
				</Card>
				
				{/* Logo and Company Name */}
				<Box 
					sx={{ 
						display: 'flex', 
						alignItems: 'center', 
						justifyContent: 'center',
						gap: 1,
						mt: 3,
						opacity: 0.8
					}}
				>
					<img
						src="/logo_inttech.png"
						alt="Int-Tech Logo"
						style={{
							width: '20px',
							height: '20px',
							objectFit: 'contain',
						}}
					/>
					<Typography
						variant="body2"
						component="div"
						sx={{
							fontFamily: 'Conthrax, monospace',
							fontWeight: 600,
							letterSpacing: '.08rem',
							color: '#666',
							fontSize: '0.85rem',
						}}
					>
						INT-TECH
					</Typography>
				</Box>
			</Container>
		</Box>
	);
}